import React, { FC, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import SearchAddressActions from 'Modules/SearchAddress/actions';
import { getCurrentCity } from 'Modules/SearchAddress/selectors';
import ThemeButton from 'Components/ThemedButton';
import { ICity } from 'Modules/SearchAddress/types';
import { mediaSmDown } from 'theme/helpers/css';
import { getTypographyFontStyleMobile } from 'theme/selectors';
import { EnumButtonSize, EnumTheme, WLabel } from 'constants/enums';
import CitiesModal from './CitiesModal';
import { updateProvince } from 'Components/Header/CitiesButton/utils';
import useModalState from 'utils/hooks/useModalState';
import { FontSize } from 'theme';
import { CITIES_BUTTON_ID } from './constants';
import GeoLocationIcon from 'Components/Icon/GeoLocationIcon';
import Text from 'Components/Typography/Text';
import { selectByWL } from 'utils/wLabel';
import CheckCity from './CheckCity';

const Wrapper = styled.div`
  position: relative;
`;

const Button = styled(ThemeButton)`
  max-width: 230px;
  padding-left: 12px;
  padding-right: 12px;
  user-select: none;

  ${mediaSmDown`
    max-width: 200px;
    min-width: unset;
    padding-left: 8px;
    padding-right: 8px;
  `}

  i {
    margin-right: 5px;
    color: currentColor;
  }

  div {
    text-align: left;
  }
`;

const wlButtonTextFontSize = selectByWL({ [WLabel.Default]: FontSize.s, [WLabel.Globus]: FontSize.m });

const ButtonText = styled(Text).attrs({ size: wlButtonTextFontSize })`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${mediaSmDown`
    ${getTypographyFontStyleMobile(FontSize.xs)}
  `}
`;

const wlBtnTheme = selectByWL({ [WLabel.Default]: EnumTheme.white, [WLabel.Globus]: EnumTheme.textSecondary });

interface ICitiesButton {
  onCityChange?: (city: ICity) => void;
  className?: string;
  checkCity?: boolean;
}

const CitiesButton: FC<ICitiesButton> = ({ className, onCityChange, checkCity }) => {
  const currentCity = useSelector(getCurrentCity);
  const { isVisible, showModal, hideModal } = useModalState();
  const dispatch = useDispatch();

  const { name, id } = currentCity;
  const selectedCity = useMemo(() => updateProvince(name, id), [name, id]);

  const handleSelectItem = useCallback(
    (city: ICity) => {
      dispatch(SearchAddressActions.clearCurrentAddress());
      dispatch(SearchAddressActions.setCurrentCity(city));
      if (onCityChange) {
        onCityChange(city);
      }
      hideModal();
    },
    [dispatch, hideModal, onCityChange]
  );

  return (
    <Wrapper>
      <Button
        testId={CITIES_BUTTON_ID}
        use={wlBtnTheme}
        size={EnumButtonSize.normal}
        onClick={showModal}
        before={<GeoLocationIcon />}
        className={className}
      >
        <ButtonText>{selectedCity}</ButtonText>
      </Button>
      {checkCity && <CheckCity onCityChange={handleSelectItem} />}
      <CitiesModal
        currentCity={currentCity}
        isModalShown={isVisible}
        closeModal={hideModal}
        onSelectItem={handleSelectItem}
      />
    </Wrapper>
  );
};

export default memo(CitiesButton);
